import React from "react";
import {
  makeStyles,
  Paper,
  Button,
  Divider,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Dialog,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Chip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import Dropzone from "react-dropzone";
import clsx from "clsx";
import { getLang } from "app/feature/constants";
import BatchInvalidSerialsTableComponent from "./BatchInvalidSerialsTable.component";

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  header: {
    padding: "4px 0px",
    paddingLeft: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  form: {
    maxHeight: "70vh",
    overflow: "auto",
    padding: theme.spacing(2),
  },
  fileInput: {
    border: "1px dashed rgba(0, 0, 0, 0.12)",
    borderRadius: 4,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    transition: "all 0.3s ease",
  },
  activeDropzone: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(0, 128, 255, 0.08)",
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(1),
  },
  instructionCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: "#f5f5f5",
  },
  stepContent: {
    padding: theme.spacing(2),
  },
  sheetCard: {
    marginBottom: theme.spacing(2),
    border: "1px solid #e0e0e0",
  },
  successCard: {
    borderColor: theme.palette.success.main,
    backgroundColor: "rgba(76, 175, 80, 0.04)",
  },
  errorCard: {
    borderColor: theme.palette.error.main,
    backgroundColor: "rgba(244, 67, 54, 0.04)",
  },
  sheetHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  statusIcon: {
    marginRight: theme.spacing(1),
  },
  summaryBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  downloadButton: {
    marginRight: theme.spacing(1),
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText || "#fff",
    "&:hover": {
      backgroundColor: theme.palette.error.dark || theme.palette.error.main,
    },
  },
}));

export default function BatchImportPresetWinnersDialogComponent({
  isOpen,
  handleClose,
  lang,
  addAlert,
  file,
  handleFileChange,
  handleValidate,
  handleSubmit,
  handleTemplateDownload,
  handleStartOver,
  isValidating,
  isSubmitting,
  isDownloadingTemplate,
  validationResult,
  showValidationResult,
  activeStep,
  luckyDrawUuid,
  prizes,
}) {
  const classes = useStyles();

  // Helper function to process error messages
  const processErrorMessage = (errorMsg) => {
    if (!errorMsg) return "";
    
    if (typeof errorMsg === 'string' && errorMsg.startsWith('error.')) {
      const apiKey = errorMsg.substring(6);
      
      // Try to access the API data directly
      let directLookup = "";
      if (lang.apiData && lang.apiData.error && lang.apiData.error[apiKey]) {
        directLookup = lang.apiData.error[apiKey];
      }
      
      if (lang.fallbackApiData && lang.fallbackApiData.error && lang.fallbackApiData.error[apiKey]) {
        directLookup = lang.fallbackApiData.error[apiKey];
      }
      
      // If direct lookup works, return it
      if (directLookup && directLookup !== apiKey) {
        return directLookup;
      }
      
      return errorMsg;
    }
    
    return errorMsg;
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      handleFileChange(acceptedFiles[0]);
    }
  };

  const renderInstructions = () => (
    <Card className={classes.instructionCard}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {getLang(lang, "paragraph.BATCH_IMPORT_INSTRUCTIONS")}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {getLang(lang, "paragraph.BATCH_IMPORT_INSTRUCTIONS_1")}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {getLang(lang, "paragraph.BATCH_IMPORT_INSTRUCTIONS_2")}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>{getLang(lang, "paragraph.SHEET_NAMES_MUST_MATCH")}:</strong>
        </Typography>
        <Box mt={1}>
          {prizes.map((prize, index) => (
            <Chip
              key={index}
              label={prize.title || prize.name}
              size="small"
              style={{ margin: "2px", backgroundColor: "#e3f2fd" }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderFileUpload = () => (
    <Box className={classes.stepContent}>
      {renderInstructions()}
      
      <Box display="flex" mb={2}>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleTemplateDownload}
          disabled={isDownloadingTemplate}
          className={classes.downloadButton}
        >
          {isDownloadingTemplate 
            ? getLang(lang, "label.DOWNLOADING") 
            : getLang(lang, "label.DOWNLOAD_TEMPLATE")
          }
        </Button>
      </Box>

      <Dropzone
        onDrop={onDrop}
        accept=".xlsx,.xls"
        multiple={false}
        noClick={false}
        noKeyboard={false}
        disabled={false}
      >
        {({ getRootProps, getInputProps, isDragActive, open }) => (
          <div
            {...getRootProps()}
            className={clsx(classes.fileInput, {
              [classes.activeDropzone]: isDragActive,
            })}
            onClick={open}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon className={classes.uploadIcon} />
            <Typography variant="h6" gutterBottom>
              {file
                ? file.name
                : isDragActive
                ? getLang(lang, "label.DROP_FILE_HERE")
                : getLang(lang, "label.CSV_DROP_OR_CLICK")
              }
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {getLang(lang, "label.EXCEL_FILES_ONLY")} (.xlsx, .xls)
            </Typography>
          </div>
        )}
      </Dropzone>

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleValidate}
          disabled={!file || isValidating}
          startIcon={isValidating ? <CircularProgress size={20} /> : null}
        >
          {isValidating 
            ? getLang(lang, "label.VALIDATING") 
            : getLang(lang, "label.VALIDATE_FILE")
          }
        </Button>
      </Box>
    </Box>
  );

  const renderValidationResults = () => (
    <Box className={classes.stepContent}>
      <Box className={classes.summaryBox}>
        <Typography variant="h6" gutterBottom>
          {getLang(lang, "label.VALIDATION_SUMMARY")}
        </Typography>
        <Typography variant="body2">
          <strong>{getLang(lang, "label.TOTAL_SHEETS")}:</strong> {validationResult?.sheet_names?.length || 0}
        </Typography>
        <Typography variant="body2">
          <strong>{getLang(lang, "label.VALID_SERIAL_NUMBERS")}:</strong> {validationResult?.total_valid_serial_numbers || 0}
        </Typography>
        <Typography variant="body2">
          <strong>{getLang(lang, "label.MATCHED_PRIZES")}:</strong> {validationResult?.matched_prizes?.length || 0}
        </Typography>
      </Box>

      {/* Display invalid serials table if there are any validation issues */}
      <BatchInvalidSerialsTableComponent
        validationResult={validationResult}
        lang={lang}
      />

      {validationResult?.sheets && Object.entries(validationResult.sheets).map(([sheetName, result]) => (
        <Card
          key={sheetName}
          className={clsx(classes.sheetCard, {
            [classes.successCard]: result.success,
            [classes.errorCard]: !result.success,
          })}
        >
          <CardContent>
            <Box className={classes.sheetHeader}>
              <Box display="flex" alignItems="center">
                {result.success ? (
                  <CheckCircleIcon color="primary" className={classes.statusIcon} />
                ) : (
                  <ErrorIcon color="error" className={classes.statusIcon} />
                )}
                <Typography variant="h6">
                  {getLang(lang, "label.SHEET")}: {sheetName}
                </Typography>
              </Box>
              <Chip
                label={result.success ? getLang(lang, "label.VALID") : getLang(lang, "label.ERROR")}
                color={result.success ? "primary" : "default"}
                className={result.success ? "" : classes.errorChip}
                size="small"
              />
            </Box>
            
            {result.prize_name && (
              <Typography variant="body2" color="textSecondary">
                <strong>{getLang(lang, "label.MATCHED_PRIZE")}:</strong> {result.prize_name}
              </Typography>
            )}
            
            {result.success && result.data?.serial_numbers && (
              <Typography variant="body2" color="textSecondary">
                <strong>{getLang(lang, "label.SERIAL_NUMBERS_FOUND")}:</strong> {result.data.serial_numbers.length}
              </Typography>
            )}
            
            {!result.success && (
              <Typography variant="body2" color="error">
                <strong>{getLang(lang, "label.ERROR")}:</strong> {processErrorMessage(result.message || result.error)}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}

      <Box mt={2} display="flex" justifyContent="space-between">
        <Button onClick={handleStartOver}>
          {getLang(lang, "label.START_OVER")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!validationResult?.success || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting 
            ? getLang(lang, "label.IMPORTING") 
            : getLang(lang, "label.IMPORT_WINNERS")
          }
        </Button>
      </Box>
    </Box>
  );

  const steps = [
    {
      label: getLang(lang, "label.UPLOAD_FILE"),
      content: renderFileUpload,
    },
    {
      label: getLang(lang, "label.REVIEW_VALIDATION"),
      content: renderValidationResults,
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="batch-import-dialog-title"
      maxWidth={false}
    >
      <Paper className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h6" id="batch-import-dialog-title">
            {getLang(lang, "label.BATCH_IMPORT_PRESET_WINNERS")}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        
        <Box className={classes.form}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {step.content()}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>
    </Dialog>
  );
} 